<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="560"
    content-class="global-search-dialog"
    @keydown.esc="$emit('input', false)"
  >
    <v-card class="global-search-card">
      <v-text-field
        ref="searchInput"
        v-model="query"
        :placeholder="$t('common.searchPages')"
        prepend-inner-icon="mdi-magnify"
        hide-details
        dense
        outlined
        autofocus
        aria-label="Search pages"
        class="global-search-input"
        @keydown.down.prevent="moveDown"
        @keydown.up.prevent="moveUp"
        @keydown.enter.prevent="selectActive"
      >
        <template #append>
          <kbd class="search-hint-kbd">ESC</kbd>
        </template>
      </v-text-field>
      <v-divider />
      <v-list
        v-if="filteredItems.length > 0"
        dense
        class="global-search-list pa-0"
      >
        <v-list-item
          v-for="(item, index) in filteredItems"
          :key="item.route"
          :class="{ 'search-active': index === activeIndex }"
          class="global-search-item"
          @click="selectItem(item)"
          @mouseenter="activeIndex = index"
        >
          <v-list-item-icon class="mr-3">
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.text }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action class="ml-2">
            <span class="search-category">{{ item.category }}</span>
          </v-list-item-action>
        </v-list-item>
      </v-list>
      <div v-else class="text-center pa-6 search-no-results">
        {{ $t('common.noResults') }}
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'GlobalSearch',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    menuItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      query: '',
      activeIndex: 0,
    }
  },
  computed: {
    filteredItems() {
      const q = this.query.toLowerCase().trim()
      const items = q
        ? this.menuItems.filter(
            (item) =>
              item.text.toLowerCase().includes(q) ||
              item.route.toLowerCase().includes(q)
          )
        : this.menuItems
      return items.slice(0, 10)
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.query = ''
        this.activeIndex = 0
        this.$nextTick(() => {
          this.$refs.searchInput?.focus()
        })
      }
    },
    query() {
      this.activeIndex = 0
    },
  },
  methods: {
    moveDown() {
      if (this.filteredItems.length === 0) return
      this.activeIndex = (this.activeIndex + 1) % this.filteredItems.length
    },
    moveUp() {
      if (this.filteredItems.length === 0) return
      this.activeIndex =
        (this.activeIndex - 1 + this.filteredItems.length) %
        this.filteredItems.length
    },
    selectActive() {
      if (
        this.filteredItems.length > 0 &&
        this.filteredItems[this.activeIndex]
      ) {
        this.selectItem(this.filteredItems[this.activeIndex])
      }
    },
    selectItem(item) {
      this.$emit('select', item.route)
      this.$emit('input', false)
    },
  },
}
</script>

<style>
.global-search-dialog {
  align-self: flex-start !important;
  margin-top: 80px !important;
  border-radius: 12px !important;
  overflow: hidden;
}

@media (max-width: 600px) {
  .global-search-dialog {
    margin-top: 20px !important;
  }
}
</style>

<style scoped>
.global-search-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.global-search-input {
  padding: 8px 12px 0 12px;
}

.global-search-input ::v-deep .v-input__slot {
  border: none !important;
  box-shadow: none !important;
}

.global-search-input ::v-deep fieldset {
  border: none !important;
}

.global-search-list {
  max-height: 400px;
  overflow-y: auto;
}

.global-search-item {
  min-height: 44px !important;
  transition: background 0.1s ease;
}

.search-active {
  background: rgba(55, 71, 79, 0.12) !important;
}

.theme--dark .search-active {
  background: rgba(255, 255, 255, 0.1) !important;
}

.search-category {
  font-size: 11px;
  font-weight: 500;
  color: #78909c;
  background: rgba(55, 71, 79, 0.08);
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

.theme--dark .search-category {
  background: rgba(255, 255, 255, 0.08);
  color: #90a4ae;
}

.search-hint-kbd {
  font-size: 10px;
  font-family: inherit;
  padding: 1px 5px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 3px;
  color: #78909c;
  line-height: 18px;
}

.theme--dark .search-hint-kbd {
  border-color: rgba(255, 255, 255, 0.2);
  color: #90a4ae;
}

.search-no-results {
  color: #78909c;
  font-size: 14px;
}
</style>
